import type { Client, ClientSummary } from '@/lib/types';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';

export const OVERDUE_ASSESSMENT_DAYS = 21;

export type DashboardWorkUrgency = 'overdue' | 'initial' | 'soon';

export interface DashboardWorkItem {
  client: string;
  clientId: string;
  metaLine: string;
  urgency: DashboardWorkUrgency;
  actionLabel: string;
}

export interface DashboardActivityItem {
  id: string;
  prefix: string;
  subject: string;
  subjectHref?: string;
  timestamp: string;
}

export function sumOpenAssessmentsFromSummaries(summaries: ClientSummary[]): number {
  return summaries.reduce((total, summary) => total + summary.assessmentCount, 0);
}

export function buildNeedsAttentionFromSummaries(
  clients: ReadonlyArray<
    Pick<Client, 'id' | 'name' | 'firstName' | 'lastName' | 'customId'>
  >,
  summaries: ClientSummary[],
  options?: { now?: Date; limit?: number }
): DashboardWorkItem[] {
  const now = options?.now ?? new Date();
  const limit = options?.limit ?? 5;
  const summaryByClientId = new Map(summaries.map((summary) => [summary.clientId, summary]));
  const items: DashboardWorkItem[] = [];

  for (const client of clients) {
    const summary = summaryByClientId.get(client.id);

    if (!summary || summary.assessmentCount === 0) {
      items.push({
        client: clientDisplayLabel(client),
        clientId: client.id,
        urgency: 'initial',
        actionLabel: 'Start',
        metaLine: 'No assessments yet — initial assessment needed',
      });
      continue;
    }

    if (!summary.latestAssessmentAt) {
      continue;
    }

    const assessmentDate = new Date(summary.latestAssessmentAt);
    if (isNaN(assessmentDate.getTime())) {
      continue;
    }

    const daysSince = Math.floor(
      (now.getTime() - assessmentDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSince > OVERDUE_ASSESSMENT_DAYS) {
      items.push({
        client: clientDisplayLabel(client),
        clientId: client.id,
        urgency: 'overdue',
        actionLabel: 'Start',
        metaLine: `Reassessment overdue — last visit ${daysSince} days ago`,
      });
    }
  }

  return items.slice(0, limit);
}

export function buildAssessmentActivityFromSummaries(
  clients: ReadonlyArray<Pick<Client, 'id' | 'name' | 'firstName' | 'lastName' | 'customId'>>,
  summaries: ClientSummary[],
  formatTimestamp: (date: Date) => string,
  options?: { limit?: number }
): DashboardActivityItem[] {
  const limit = options?.limit ?? 5;
  const clientNameById = new Map(clients.map((client) => [client.id, clientDisplayLabel(client)]));

  return summaries
    .filter((summary) => summary.latestAssessmentAt != null)
    .map((summary) => {
      const date = new Date(summary.latestAssessmentAt!);
      const clientName = clientNameById.get(summary.clientId) ?? 'Unknown client';
      return {
        id: `assessment-${summary.clientId}`,
        prefix: 'Last assessment for ',
        subject: clientName,
        subjectHref: `/clients/${summary.clientId}`,
        timestamp: formatTimestamp(date),
        sortDate: date,
      };
    })
    .filter((item) => !isNaN(item.sortDate.getTime()))
    .sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime())
    .slice(0, limit)
    .map(({ id, prefix, subject, subjectHref, timestamp }) => ({
      id,
      prefix,
      subject,
      subjectHref,
      timestamp,
    }));
}
